import { useSelector } from 'react-redux';
import { adminRouteAccess } from '@rsos/capstone/src/constants/adminRouteAccess';
import { selectActiveCapabilities } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import type { SinatraState } from '@rsos/sinatra';
import { useUserPermissions } from '@rsos/sinatra/src/selectors';
import { getFlag, useFlagsVersion } from '@rsos/utils/openFeature';
import { hasAccess, type AccessContext } from './hasAccess';

interface StateWithSinatra {
  sinatra: SinatraState;
}

/**
 * Resolves a descriptor's flag through the OpenFeature client, defaulting to off.
 * Module-level so the context's identity is stable across renders.
 */
const isFlagEnabled = (flagKey: string) => getFlag(flagKey, false);

/**
 * Everything `hasAccess` needs to know about the current user, read once.
 *
 * Permissions come from `currentRole.permissions`, which is the canonical
 * source: scorpius serializes one role relationship twice — as `permissions`
 * (all granted names) and as `permissions_full` (the same list as objects,
 * which sinatra filters down to `rsp_rbac: true` for pageAccessList). The names
 * list is the superset, so it answers for page-access and capability-style
 * permissions alike.
 *
 * Exposed because resolving more than one descriptor — picking a section's index
 * page, or where the top-nav Admin link should point — cannot call a per-path
 * hook in a loop.
 */
export const useAccessContext = (): AccessContext => {
  // isFlagEnabled reads without subscribing, so subscribe here.
  useFlagsVersion();

  const activeCapabilities = useSelector(selectActiveCapabilities);
  const permissions = useUserPermissions();
  const application = useSelector(
    (state: StateWithSinatra) => state.sinatra.user?.currentRole?.application,
  );

  return {
    permissions,
    isFlagEnabled,
    activeCapabilities: activeCapabilities || {},
    application,
  };
};

/**
 * Whether the current user can reach an /admin route, by path.
 *
 * @param path Key into adminRouteAccess, e.g. '/admin/access/roles'.
 */
export const useHasAccess = (path: string): boolean =>
  hasAccess(adminRouteAccess[path], useAccessContext());

/**
 * The first of `paths` the current user can reach, or undefined if none.
 *
 * `paths` is a preference order, so the caller decides what "first" means: a
 * section's index route lists its children in the order it would rather land on
 * them.
 */
export const useFirstAccessiblePath = (paths: string[]): string | undefined => {
  const context = useAccessContext();
  return paths.find(path => hasAccess(adminRouteAccess[path], context));
};

/**
 * Where the top-nav Admin link should point, or undefined when the user can
 * reach no admin page at all and the link should not render.
 *
 * Resolved from the descriptors so the link cannot offer a page the route guard
 * will immediately bounce the user off — which is what a separate
 * permission-to-path map did, knowing nothing about capabilities, flags or
 * application. The order of `adminRouteAccess` is the preference order.
 */
export const useAdminLandingPath = (): string | undefined =>
  useFirstAccessiblePath(Object.keys(adminRouteAccess));

export default useHasAccess;
